import { useEffect, useState } from "react";
import { ApiError, api, type Goal, type LifeStatus, type Priority, type UserView } from "../api";
import { ASSISTANT_NAME } from "../brand";
import { deviceTimezone, firstName, referralCode } from "../platform";
import { DateWheel, isFullDate } from "../ui";

type Step = "splash" | "lifeStatus" | "priority" | "pain" | "building";

const LIFE_STATUS_OPTIONS: { id: LifeStatus; label: string }[] = [
  { id: "free", label: "Свободен(на)" },
  { id: "in_love", label: "Влюблён(а)" },
  { id: "in_relationship", label: "В отношениях" },
  { id: "complicated", label: "Всё сложно" },
];

const PRIORITY_OPTIONS: { id: Priority; label: string }[] = [
  { id: "love", label: "Любовь и отношения" },
  { id: "money", label: "Деньги и работа" },
  { id: "self", label: "Я сам(а): мой путь" },
  { id: "all", label: "Всё сразу" },
];

/** Та же схема (goal), что и раньше — только переформулирована под боли клиента, а не абстрактную цель. */
const PAIN_OPTIONS: { id: Goal; label: string }[] = [
  { id: "self", label: "Не понимаю, чего хочу и куда двигаться" },
  { id: "relationships", label: "Сложности в отношениях" },
  { id: "money", label: "Нестабильность с деньгами" },
  { id: "gradually", label: "Всего понемногу, не могу выделить главное" },
];

function OptionStep({
  title,
  step,
  options,
  onPick,
}: {
  title: string;
  step: number;
  options: { id: string; label: string }[];
  onPick: (id: string) => void;
}) {
  return (
    <div className="center">
      <div className="onboard-dots">
        {[0, 1, 2].map((i) => (
          <span key={i} className={i === step ? "on" : ""} />
        ))}
      </div>
      <h1>{title}</h1>
      <div className="onboard-options">
        {options.map((o) => (
          <button key={o.id} type="button" onClick={() => onPick(o.id)}>
            {o.label}
          </button>
        ))}
      </div>
      <p className="onboard-hint">Так разбор будет точнее</p>
    </div>
  );
}

/** Восьмиугольная диаграмма-прелюдия: простые числа для эффекта, пока настоящий разбор считается на сервере. */
function BuildingMatrix({ name, date, onFinished }: { name: string; date: string; onFinished: () => void }) {
  const [shown, setShown] = useState(0);
  const digitSum = (n: number): number => {
    let x = Math.abs(n);
    while (x > 9) x = String(x).split("").reduce((a, c) => a + Number(c), 0);
    return x || 9;
  };
  const [d, m, y] = date.split(".").map(Number) as [number, number, number];
  const vals = [digitSum(d + m), digitSum(m + y), digitSum(d + y), digitSum(d), digitSum(m), digitSum(y), digitSum(d * m), digitSum(d + m + y)];
  const center = digitSum(vals.reduce((a, b) => a + b, 0));
  const positions: [number, number][] = [
    [140, 20], [220, 55], [258, 130], [220, 205], [140, 240], [60, 205], [22, 130], [60, 55],
  ];

  useEffect(() => {
    const steps = vals.length + 1;
    let i = 0;
    const id = window.setInterval(() => {
      i += 1;
      setShown(i);
      if (i >= steps) {
        window.clearInterval(id);
        window.setTimeout(onFinished, 500);
      }
    }, 220);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="center">
      <p className="building-kicker">строю матрицу</p>
      <h1>{name || "твою"}</h1>
      <svg className="building-svg" viewBox="0 0 280 260" role="img" aria-label="Матрица строится">
        <g fill="none" stroke="var(--accent)" strokeWidth="1" opacity="0.4">
          <polygon points={positions.map((p) => p.join(",")).join(" ")} />
        </g>
        {positions.map(([x, y], i) => (
          <g key={i} className={i < shown ? "bm-node on" : "bm-node"} style={{ transitionDelay: `${i * 0.02}s` }}>
            <circle cx={x} cy={y} r={22} />
            <text x={x} y={y} textAnchor="middle" dominantBaseline="central">
              {vals[i]}
            </text>
          </g>
        ))}
        <g className={shown > vals.length ? "bm-node on bm-center" : "bm-node bm-center"}>
          <circle cx={140} cy={130} r={28} />
          <text x={140} y={130} textAnchor="middle" dominantBaseline="central">
            {center}
          </text>
        </g>
      </svg>
    </div>
  );
}

export function Birth({ onDone }: { onDone: (u: UserView, trialStarted: boolean) => void }) {
  const [step, setStep] = useState<Step>("splash");
  const [lifeStatus, setLifeStatus] = useState<LifeStatus | null>(null);
  const [priority, setPriority] = useState<Priority | null>(null);
  const [goal, setGoal] = useState<Goal | null>(null);
  const [date, setDate] = useState("");
  const [name, setName] = useState(firstName() ?? "");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ user: UserView; trialStarted: boolean } | null>(null);
  const [animDone, setAnimDone] = useState(false);

  useEffect(() => {
    if (animDone && result) onDone(result.user, result.trialStarted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [animDone, result]);

  const startBuild = async () => {
    setStep("building");
    setError(null);
    setAnimDone(false);
    setResult(null);
    try {
      const r = await api.setBirth(date, deviceTimezone(), {
        ...(lifeStatus ? { lifeStatus } : {}),
        ...(priority ? { priority } : {}),
        ...(goal ? { goal } : {}),
        ...(referralCode() ? { referralCode: referralCode() } : {}),
      });
      setResult({ user: r.user, trialStarted: r.trial.started });
    } catch (e) {
      setError(
        e instanceof ApiError && e.status === 400
          ? "Не получилось разобрать дату. Проверьте день, месяц и год."
          : "Не удалось сохранить. Попробуйте ещё раз.",
      );
      setStep("splash");
    }
  };

  if (step === "splash") {
    return (
      <div className="center">
        <p className="onboard-brand">{ASSISTANT_NAME}</p>
        <div className="onboard-moon" aria-hidden />
        <h1>Узнай, что сейчас происходит в твоей жизни</h1>
        <p>Нужна только дата рождения. Первый разбор бесплатный.</p>
        <div className="birth">
          <label className="field">
            <span>Имя</span>
            <input className="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Как к тебе обращаться" />
          </label>
          <label className="field">
            <span>Дата рождения</span>
          </label>
          <DateWheel value={date} onChange={setDate} />
          {error && <div className="error">{error}</div>}
          <button className="primary" disabled={!isFullDate(date)} onClick={() => setStep("lifeStatus")}>
            Дальше
          </button>
        </div>
      </div>
    );
  }

  if (step === "lifeStatus") {
    return (
      <OptionStep
        title="Что у тебя в личной жизни?"
        step={0}
        options={LIFE_STATUS_OPTIONS}
        onPick={(id) => {
          setLifeStatus(id as LifeStatus);
          setStep("priority");
        }}
      />
    );
  }

  if (step === "priority") {
    return (
      <OptionStep
        title={name ? `${name}, что для тебя сейчас важнее всего?` : "Что для тебя сейчас важнее всего?"}
        step={1}
        options={PRIORITY_OPTIONS}
        onPick={(id) => {
          setPriority(id as Priority);
          setStep("pain");
        }}
      />
    );
  }

  if (step === "pain") {
    return (
      <OptionStep
        title={name ? `${name}, что сейчас больше всего беспокоит?` : "Что сейчас больше всего беспокоит?"}
        step={2}
        options={PAIN_OPTIONS}
        onPick={(id) => {
          setGoal(id as Goal);
          void startBuild();
        }}
      />
    );
  }

  return <BuildingMatrix name={name} date={date} onFinished={() => setAnimDone(true)} />;
}
